import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { COLLAPSED_VISIBLE_COUNT } from '../models/healthCheck.constants'
import type {
  HealthCheckCategory,
  HealthCheckCounts,
  HealthCheckFinding,
} from '../models/healthCheck.types'
import { categoryOf, groupFindings } from '../service/grouping'

import type { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

function emptyExpanded(): Record<HealthCheckCategory, boolean> {
  return { needs_attention: false, suggestions: false, dismissed: false, other: false }
}

export const useHealthCheckStore = defineStore('healthChecks', () => {
  const findings = ref<HealthCheckFinding[] | null>(null)
  const status = ref<UiAsyncState>('loading')
  const error = ref<ApiError | null>(null)
  const expanded = ref(emptyExpanded())
  const selectedId = ref<number | null>(null)
  const triagePending = ref<Record<number, boolean>>({})
  const actionPending = ref<Record<number, boolean>>({})
  // What a Meta checkpoint verify answered, per card — shown inline next to its buttons.
  const checkpointNotices = ref<Record<number, string>>({})

  const grouped = computed(() => groupFindings(findings.value ?? []))
  const counts = computed<HealthCheckCounts>(() => ({
    needs_attention: grouped.value.needs_attention.length,
    suggestions: grouped.value.suggestions.length,
    dismissed: grouped.value.dismissed.length,
    other: grouped.value.other.length,
  }))
  const selected = computed(
    () => findings.value?.find((finding) => finding.id === selectedId.value) ?? null,
  )
  const isDrawerOpen = computed(() => selectedId.value !== null)
  const isEmpty = computed(() => status.value === 'empty')

  function visible(category: HealthCheckCategory): HealthCheckFinding[] {
    const list = grouped.value[category]
    return expanded.value[category] ? list : list.slice(0, COLLAPSED_VISIBLE_COUNT)
  }

  function canExpand(category: HealthCheckCategory): boolean {
    return grouped.value[category].length > COLLAPSED_VISIBLE_COUNT
  }

  function setLoading(): void {
    status.value = 'loading'
    error.value = null
  }

  function setFindings(next: HealthCheckFinding[]): void {
    findings.value = next
    // A reload may legitimately remove a finding (for example after server-side remediation or
    // stale-triage recovery). Keep the drawer invariant strong: open always implies selected.
    if (selectedId.value !== null && !next.some((finding) => finding.id === selectedId.value)) {
      selectedId.value = null
    }
    status.value = next.length > 0 ? 'ready' : 'empty'
    error.value = null
  }

  function setError(next: ApiError): void {
    error.value = next
    status.value = 'error'
  }

  function toggleExpanded(category: HealthCheckCategory): void {
    expanded.value[category] = !expanded.value[category]
  }

  function select(id: number): void {
    selectedId.value = id
  }

  function clearSelection(): void {
    selectedId.value = null
  }

  function applyTriage(id: number, dismissedAt: string | null): void {
    const finding = findings.value?.find((item) => item.id === id)
    if (!finding) return
    finding.dismissedAt = dismissedAt
    finding.isDismissed = Boolean(dismissedAt)
    finding.category = categoryOf(finding)
  }

  function setTriagePending(id: number, pending: boolean): void {
    if (pending) triagePending.value[id] = true
    else delete triagePending.value[id]
  }

  function setActionPending(id: number, pending: boolean): void {
    if (pending) actionPending.value[id] = true
    else delete actionPending.value[id]
  }

  function setCheckpointNotice(id: number, message: string | null): void {
    if (message) checkpointNotices.value[id] = message
    else delete checkpointNotices.value[id]
  }

  function resetState(): void {
    findings.value = null
    status.value = 'loading'
    error.value = null
    expanded.value = emptyExpanded()
    selectedId.value = null
    triagePending.value = {}
    actionPending.value = {}
    checkpointNotices.value = {}
  }

  return {
    findings,
    status,
    error,
    expanded,
    selectedId,
    triagePending,
    actionPending,
    checkpointNotices,
    grouped,
    counts,
    selected,
    isDrawerOpen,
    isEmpty,
    visible,
    canExpand,
    setLoading,
    setFindings,
    setError,
    toggleExpanded,
    select,
    clearSelection,
    applyTriage,
    setTriagePending,
    setActionPending,
    setCheckpointNotice,
    resetState,
  }
})

export type HealthCheckStore = ReturnType<typeof useHealthCheckStore>
